/**
 * An AI call campaign's name, kind, angle, state and daily cap.
 *
 * The kind feeds the conversation (a warm call may refer to what we sent); it
 * never decides the opening's reason sentence, which follows what actually
 * reached each company. A new campaign starts from one of two presets, which
 * set the kind, the goal and the audience in one press.
 *
 * The angle (ring R9) is why the AI calls: an offer, or an alert about a defect
 * on the company's website. Picking the alert also sets the goal, the question
 * and the audience an alert call needs, and picking the offer again puts back
 * what that overwrote (`withAngle`).
 */
import { Label, RadioGroup, RadioGroupItem } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  CAMPAIGN_ANGLES,
  CAMPAIGN_KINDS,
  CAMPAIGN_LIMITS,
  CAMPAIGN_STATUSES,
  QUEUE_DAYS,
  readDailyCap,
  withAngle,
  type CampaignAngle,
  type CampaignDraft,
  type CampaignKind,
  type CampaignStatus,
} from "../../../../lib/aiCampaigns";
import { Btn, PillTabs, SelectInput } from "../../../ui";
import { Field, Panel } from "../../marketing/shared";
import { Pill } from "../../settings/panel/fields";

export function CampaignBasicsCard({
  draft,
  onChange,
  isNew,
  disabled,
  onPreset,
}: {
  draft: CampaignDraft;
  onChange: (next: CampaignDraft) => void;
  isNew: boolean;
  disabled?: boolean;
  onPreset: (kind: CampaignKind) => void;
}) {
  const t = useT();
  const kindTabs = CAMPAIGN_KINDS.map((value) => ({ value, label: t(`aiCalls.campaigns.kind.${value}`) }));
  const capInvalid = readDailyCap(draft.dailyCap) === null;
  const isAngle = (value: string): value is CampaignAngle => (CAMPAIGN_ANGLES as readonly string[]).includes(value);

  return (
    <Panel title={t("aiCalls.campaigns.basics.title")}>
      <div className="space-y-4">
        {isNew ? (
          <div>
            <p className="crm-lab !mt-0">{t("aiCalls.campaigns.basics.presets")}</p>
            <div className="flex flex-wrap gap-2">
              {CAMPAIGN_KINDS.map((kind) => (
                <Btn key={kind} type="button" onClick={() => onPreset(kind)} disabled={disabled}>
                  {t(`aiCalls.campaigns.preset.${kind}`)}
                </Btn>
              ))}
            </div>
            <p className="crm-sub mt-1.5 text-xs">{t("aiCalls.campaigns.basics.presetsHint")}</p>
          </div>
        ) : null}

        <Field label={t("aiCalls.campaigns.basics.name")}>
          <Pill
            value={draft.name}
            placeholder={t("aiCalls.campaigns.basics.namePlaceholder")}
            disabled={disabled}
            onChange={(name) => onChange({ ...draft, name: name.slice(0, CAMPAIGN_LIMITS.name) })}
          />
        </Field>

        <div>
          <p className="crm-lab !mt-0">{t("aiCalls.campaigns.basics.kind")}</p>
          <PillTabs
            tabs={kindTabs}
            value={draft.kind}
            onChange={(kind) => {
              if (!disabled) onChange({ ...draft, kind });
            }}
            small
          />
          <p className="crm-sub mt-1.5 text-xs">{t(`aiCalls.campaigns.basics.kindHint.${draft.kind}`)}</p>
        </div>

        <div>
          <p className="crm-lab !mt-0" id="campaign-angle-label">
            {t("aiCalls.campaigns.basics.angle")}
          </p>
          <RadioGroup
            aria-labelledby="campaign-angle-label"
            className="flex flex-row flex-wrap gap-x-5 gap-y-2"
            value={draft.angle}
            disabled={disabled}
            onValueChange={(value) => {
              if (!disabled && isAngle(value)) onChange(withAngle(draft, value));
            }}
          >
            {CAMPAIGN_ANGLES.map((angle) => (
              <div key={angle} className="flex items-center gap-2">
                <RadioGroupItem value={angle} id={`campaign-angle-${angle}`} />
                <Label htmlFor={`campaign-angle-${angle}`} className="text-[13px] font-normal">
                  {t(`aiCalls.campaigns.angle.${angle}`)}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <p className="crm-sub mt-1.5 text-xs">{t(`aiCalls.campaigns.basics.angleHint.${draft.angle}`)}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("aiCalls.campaigns.basics.dailyCap")}
            hint={
              capInvalid
                ? t("aiCalls.campaigns.basics.dailyCapInvalid", { min: CAMPAIGN_LIMITS.dailyCap.min, max: CAMPAIGN_LIMITS.dailyCap.max })
                : t("aiCalls.campaigns.basics.dailyCapHint", { days: QUEUE_DAYS })
            }
          >
            <Pill
              value={draft.dailyCap}
              inputMode="numeric"
              disabled={disabled}
              onChange={(dailyCap) => onChange({ ...draft, dailyCap: dailyCap.replace(/[^\d]/g, "").slice(0, 3) })}
            />
          </Field>
          {!isNew ? (
            <Field label={t("aiCalls.campaigns.basics.status")} hint={t("aiCalls.campaigns.basics.statusHint")}>
              <SelectInput
                value={draft.status}
                onChange={(status) => onChange({ ...draft, status: status as CampaignStatus })}
                label={t("aiCalls.campaigns.basics.status")}
                disabled={disabled}
              >
                {CAMPAIGN_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`aiCalls.campaigns.status.${status}`)}
                  </option>
                ))}
              </SelectInput>
            </Field>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
